import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ViewProps } from 'react-native';
import { PixelRatio, Platform } from 'react-native';

import { useLaunchDarkly } from '@order/LaunchDarkly';
import { getAppVersion, getCurrentUrl } from '@order/utils';

import {
  type LaunchDarklyCustomEvent,
  launchDarklyEventOverrides,
  type MixpanelEvent,
} from './events-overrides';
import { googleAnalyticsEventOverrides, mixpanelEventOverrides } from './events-overrides';
import { telemetryEventLogger as eventLogger, telemetryLogger as logger } from './Telemetry.logger';
import { postEventHandlers, preEventHandlers } from './TelemetryEventHandlers';
import {
  checkIfExperimentExposureTracked,
  markExperimentExposureTracked,
  resetExperimentExposures,
} from './TelemetryExperimentExposure';
import { googleAnalytics, MIXPANEL_USER_PROPERTY_NAMES, useMixpanel } from './vendors';

import type { TelemetryEvent, TelemetryEventName, TelemetryTrackParams } from './TelemetryEvent';
import type { TelemetryEventHandlerProps } from './TelemetryEventHandlers';
import type { TelemetryEventOverride, TelemetryEventOverrideProps } from './TelemetryEventOverride';
import type { Mixpanel } from './vendors/mixpanel/Mixpanel.types';

// ────────────────────────────────────────────────────────────────────────────────

const TelemetryContext = createContext<Telemetry | undefined>(undefined);

export const TelemetryProvider = (props: Pick<ViewProps, 'children'>) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<TelemetryUser | undefined>();

  const [mixpanelFeatureFlags, setMixpanelFeatureFlags] = useState<MixpanelFeatureFlags>({});

  // ─── Refs ──────────────────────────────────────────────────────────────────

  /**
   * Read at track time instead of captured in closures, so `track` and other helpers
   * keeps a stable identity across login and logout while still reporting the
   * current user.
   */
  const userIdRef = useRef<string | undefined>(undefined);

  // ─── Launch Darkly ─────────────────────────────────────────────────────────

  // @ts-expect-error track property may not exist on LaunchDarklyProviderContextType
  const { track: launchDarklyTrack } = useLaunchDarkly();

  // ─── Mixpanel ──────────────────────────────────────────────────────────────

  const {
    getAllFeatureFlagVariants: mixpanelGetAllFeatureFlagVariants,
    getFeatureFlagValue,
    initialize: mixpanelInitialize,
    identify: mixpanelIdentify,
    reset: mixpanelReset,
    setSuperProperty: mixpanelSetSuperProperty,
    setUserProperty: mixpanelSetUserProperty,
    track: mixpanelTrack,
  } = useMixpanel();

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const setSuperProperty = useCallback(
    (key: string, value: unknown) => {
      logger.info(`setSuperProperty(${key}, ${JSON.stringify(value)})`);

      mixpanelSetSuperProperty(key, value);
    },
    [mixpanelSetSuperProperty],
  );

  const setCustomerProperty = useCallback(
    (key: string, value: unknown) => {
      logger.info(`setCustomerProperty(${key}, ${JSON.stringify(value)})`);

      mixpanelSetUserProperty(key, value);
    },
    [mixpanelSetUserProperty],
  );

  const setPersistentProperties = useCallback(() => {
    logger.info('setPersistentProperties()');

    setSuperProperty('platform', Platform.OS);
    setCustomerProperty('pixelRatio', PixelRatio.get());
    setCustomerProperty('fontScale', PixelRatio.getFontScale());
  }, [setSuperProperty, setCustomerProperty]);

  const refreshMixpanelFeatureFlags = useCallback(async () => {
    const variants = await mixpanelGetAllFeatureFlagVariants();

    setMixpanelFeatureFlags(Object.fromEntries(variants));
  }, [mixpanelGetAllFeatureFlagVariants]);

  const initialize = useCallback(
    async (params: TelemetryInitializeParams) => {
      const { isMixpanelFeatureFlagsEnabled } = params;

      logger.info('initialize()');

      try {
        await mixpanelInitialize({ isFeatureFlagsEnabled: isMixpanelFeatureFlagsEnabled });
        setPersistentProperties();
      } catch (error: unknown) {
        logger.error(error);
      }

      try {
        googleAnalytics.initialize();
      } catch (error: unknown) {
        logger.error(error);
      }

      setIsInitialized(true);
    },
    [mixpanelInitialize, setPersistentProperties],
  );

  const identify = useCallback(
    async (telemetryUser: TelemetryUser) => {
      logger.info('identify()');

      userIdRef.current = telemetryUser.id;
      setUser(telemetryUser);

      const { id, firstName, lastName, email, trackingUuid } = telemetryUser;

      if (id) {
        await mixpanelIdentify(id);

        const name = [firstName, lastName].filter(Boolean).join(' ');

        mixpanelSetUserProperty(MIXPANEL_USER_PROPERTY_NAMES.appVersion, getAppVersion());

        if (name !== '') {
          mixpanelSetUserProperty(MIXPANEL_USER_PROPERTY_NAMES.name, name);
        }

        if (email) {
          mixpanelSetUserProperty(MIXPANEL_USER_PROPERTY_NAMES.email, email);
        }

        if (trackingUuid) {
          mixpanelSetUserProperty(MIXPANEL_USER_PROPERTY_NAMES.trackingUuid, trackingUuid);
        }
      }
    },
    [mixpanelIdentify, mixpanelSetUserProperty],
  );

  const reset = useCallback(() => {
    logger.info('reset');

    userIdRef.current = undefined;
    setUser(undefined);

    mixpanelReset();
    googleAnalytics.resetAnalyticsData();
    resetExperimentExposures();

    setPersistentProperties();
  }, [mixpanelReset, setPersistentProperties]);

  const getTrackEventContext = useCallback(
    () => ({ userId: userIdRef.current, setSuperProperty, setCustomerProperty }),
    [setSuperProperty, setCustomerProperty],
  );

  const handleMixpanelEvent = useCallback(
    (event: MixpanelEvent) => {
      mixpanelTrack(event.name, event.payload);
    },
    [mixpanelTrack],
  );

  const handleDefaultMixpanelEvent = useCallback(
    <Name extends TelemetryEventName>(event: TelemetryEvent<Name>) => {
      mixpanelTrack(event.name, event.payload as Record<string, unknown> | undefined);
    },
    [mixpanelTrack],
  );

  const handleLaunchDarklyEvent = useCallback(
    (event: LaunchDarklyCustomEvent) => {
      launchDarklyTrack(event.name, event.payload);
    },
    [launchDarklyTrack],
  );

  const trackEvent = useCallback(
    <Name extends TelemetryEventName>(event: TelemetryEvent<Name>) => {
      const { name, payload } = event;

      eventLogger.info(name, payload);
      logger.info(`┌─ trackEvent(${name}, ...)`);

      const trackEventContext = getTrackEventContext();

      const overrideProps: TelemetryEventOverrideProps<Name> = {
        context: {
          ...trackEventContext,
          ...(payload as Record<string, unknown>),
        },
        event,
      };

      const generalProps: TelemetryEventHandlerProps = {
        context: {
          ...trackEventContext,
          ...(payload as Record<string, unknown>),
        },
        name,
        payload,
      };

      // ─── Pre-event handlers ──────────────────────

      preEventHandlers.forEach((handler) => {
        handler(generalProps);
      });

      // ─── Launch Darkly ───────────────────────────

      handleTelemetryEvent(
        overrideProps,
        launchDarklyEventOverrides[name],
        handleLaunchDarklyEvent,
      );

      // ─── Mixpanel ────────────────────────────────

      handleTelemetryEvent(
        overrideProps,
        mixpanelEventOverrides[name],
        handleMixpanelEvent,
        handleDefaultMixpanelEvent,
      );

      // ─── Google Analytics ────────────────────────

      handleTelemetryEvent(
        overrideProps,
        googleAnalyticsEventOverrides[name],
        googleAnalytics.track,
      );

      // ─── Post-event handlers ─────────────────────

      postEventHandlers.forEach((handler) => {
        handler(generalProps);
      });

      logger.info('└─');
    },
    [
      handleDefaultMixpanelEvent,
      handleLaunchDarklyEvent,
      handleMixpanelEvent,
      getTrackEventContext,
    ],
  );

  const track = useCallback(
    <Name extends TelemetryEventName>(...[name, payload]: TelemetryTrackParams<Name>) => {
      trackEvent({ name, payload } as TelemetryEvent<Name>);
    },
    [trackEvent],
  );

  /** Reports a Mixpanel experiment exposure once per user, experiment and variant per session. */
  const trackExperimentStarted = useCallback(
    (params: TelemetryExperimentStartedParams) => {
      const { experimentName, variantName } = params;

      const exposure = { userId: userIdRef.current, experimentName, variantName };

      if (checkIfExperimentExposureTracked(exposure)) {
        logger.info(`skip trackExperimentStarted(${experimentName}, ${variantName})`);

        return;
      }

      markExperimentExposureTracked(exposure);

      const payload = { 'Experiment name': experimentName, 'Variant name': variantName };

      eventLogger.info(EXPERIMENT_STARTED_EVENT_NAME, payload);
      logger.info(`trackExperimentStarted(${experimentName}, ${variantName})`);

      mixpanelTrack(EXPERIMENT_STARTED_EVENT_NAME, payload);
    },
    [mixpanelTrack],
  );

  const value = useMemo(
    () => ({
      isInitialized,
      mixpanelFeatureFlags,
      user,
      initialize,
      identify,
      reset,
      setSuperProperty,
      setCustomerProperty,
      trackEvent,
      track,
      trackExperimentStarted,
      getFeatureFlagValue,
      refreshMixpanelFeatureFlags,
    }),
    [
      isInitialized,
      mixpanelFeatureFlags,
      user,
      initialize,
      identify,
      reset,
      setSuperProperty,
      setCustomerProperty,
      trackEvent,
      track,
      trackExperimentStarted,
      getFeatureFlagValue,
      refreshMixpanelFeatureFlags,
    ],
  );

  return <TelemetryContext.Provider value={value}>{props.children}</TelemetryContext.Provider>;
};

/**
 * Handles a telemetry event for a vendor.
 *
 * @param props Object containing the handler context and the telemetry event.
 * @param telemetryEventOverride A function that returns an object with a pre- and post-event handler, and a vendor-specific event object.
 * @param vendorEventHandler Performs sending the vendor-specific event.
 * @param defaultHandler Performs sending the vendor-specific event if `telemetryEventHandler` is undefined.
 */
const handleTelemetryEvent = <Name extends TelemetryEventName, VendorEvent>(
  props: TelemetryEventOverrideProps<Name>,
  telemetryEventOverride: TelemetryEventOverride<Name, VendorEvent> | undefined,
  vendorEventHandler: (vendorEvent: VendorEvent) => void,
  defaultHandler?: (telemetryEvent: TelemetryEvent<Name>) => void,
) => {
  // If no override is defined, use the default handler, if specified.
  if (!telemetryEventOverride) {
    defaultHandler?.(props.event);

    return;
  }

  const overrideResult = telemetryEventOverride(props);

  // If the override result is undefined, skip handling of the event altogether.
  if (!overrideResult) return;

  const { pre: preEventHandler, event: vendorEvent, post: postEventHandler } = overrideResult;

  // Execute the pre-event handler, if defined.
  preEventHandler?.();

  // Execute the vendor event handler, if defined.
  if (vendorEvent) {
    vendorEventHandler(vendorEvent);
  }

  // Execute the post-event handler, if defined.
  postEventHandler?.();
};

export const useTelemetry = () => {
  const context = useContext(TelemetryContext);

  if (context === undefined) {
    throw new Error('useTelemetry must be used within a <TelemetryProvider>');
  }

  return context;
};

export const useTrackEffect = <Name extends TelemetryEventName>(
  ...[name, payload]: TelemetryTrackParams<Name>
) => {
  useTrackEventEffect({ name, payload } as TelemetryEvent<Name> & TelemetrySkip);
};

export const useTrackEventEffect = <Name extends TelemetryEventName>(
  event: TelemetryEvent<Name> & TelemetrySkip,
) => {
  const { trackEvent } = useTelemetry();
  const { skip, ...rest } = event;

  useEffect(() => {
    if (skip) return;

    trackEvent(rest as TelemetryEvent<Name>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);
};

export function useTrackScreen() {
  const { track } = useTelemetry();

  const trackScreen = useCallback(
    (pathname: string) => {
      track('app.page_view', {
        pageTitle: pathname,
        pageLocation: getCurrentUrl(),
      });

      switch (pathname) {
        case '/join-or-sign-in': {
          track('sign-in.view');
          break;
        }

        case '/':
        case '/home': {
          track('home.view');
          break;
        }

        case '/scan-at-checkout': {
          // @ts-expect-error TS2345: Argument of type '"scan-to-pay.view"' is not assignable to parameter of typ
          track('scan-to-pay.view');
          break;
        }

        case '/dietary-restrictions': {
          track('dietary-restrictions.view');
          break;
        }

        case '/account':
        case '/account/menu': {
          track('account.view', undefined);
          break;
        }

        case '/account/info': {
          track('account.view', { screen: 'profile' });
          break;
        }

        case '/account/addresses': {
          track('account.view', { screen: 'addresses' });
          break;
        }

        case '/account/orders': {
          track('account.view', { screen: 'orders' });
          break;
        }

        case '/account/favorites': {
          track('account.view', { screen: 'favorites' });
          break;
        }

        case '/account/dietary-restrictions': {
          track('account.view', { screen: 'dietary-restrictions' });
          break;
        }

        default: {
          if (pathname.startsWith('/account/orders/')) {
            track('account.view', { screen: 'orders' });
          }

          break;
        }
      }
    },
    [track],
  );

  return { trackScreen };
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const EXPERIMENT_STARTED_EVENT_NAME = '$experiment_started';

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type Telemetry = Readonly<{
  isInitialized: boolean;
  mixpanelFeatureFlags: MixpanelFeatureFlags;
  user?: TelemetryUser;
  initialize: (params: TelemetryInitializeParams) => Promise<void>;
  identify: (user: TelemetryUser) => Promise<void>;
  reset: () => void;
  setSuperProperty: (key: string, value: unknown) => void;
  setCustomerProperty: (key: string, value: unknown) => void;
  trackEvent: <Name extends TelemetryEventName>(event: TelemetryEvent<Name>) => void;
  track: <Name extends TelemetryEventName>(...[name, payload]: TelemetryTrackParams<Name>) => void;
  trackExperimentStarted: (params: TelemetryExperimentStartedParams) => void;
  getFeatureFlagValue: <Value>(featureFlagKey: string, fallbackValue: Value) => Promise<Value>;
  refreshMixpanelFeatureFlags: () => Promise<void>;
}>;

type TelemetryInitializeParams = {
  isMixpanelFeatureFlagsEnabled: boolean;
};

type TelemetryExperimentStartedParams = {
  experimentName: string;
  variantName: string;
};

type MixpanelFeatureFlags = Record<
  string,
  Awaited<ReturnType<Mixpanel['getAllFeatureFlagVariants']>> extends Map<string, infer Variant>
    ? Variant
    : never
>;

type TelemetryUser = Readonly<{
  id?: string;
  trackingUuid?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}>;

export type TelemetrySkip = Readonly<{
  skip?: boolean;
}>;
